import { FirebaseError } from 'firebase/app'
import { signInWithCustomToken } from 'firebase/auth'
import { ApiError, api } from './api'
import { auth, signOut } from './firebase'

/**
 * Sign-in with SightHub: an employee's AD account, through authentik (password, then
 * TOTP). The backend runs that flow, finds or creates the Firebase user by the AD
 * email, and hands back a custom token — so from there on this is an ordinary Firebase
 * session, and `/me`, the `role` claim and the Storage rules work as after Google.
 */

/** Leave for authentik. It needs the whole window: a navigation, never a fetch. */
export function signInWithSightHub() {
  const { origin, pathname, search } = window.location
  // The backend returns only to pages of wiki.sighthub.cloud, so from `npm run dev`
  // this ends on `400 bad_return` — the round trip can only be tried on the live site.
  window.location.assign(api.ssoStartUrl(origin + pathname + search))
}

let handoff: Promise<string | null> | undefined

/**
 * Finish a SightHub sign-in that came back in the URL hash — once per page load, no
 * matter how often this is called. Resolves to why it failed, or `null` when it
 * worked or there was nothing to finish.
 *
 * It never rejects, because AuthProvider waits on it before listening for a session
 * at all: otherwise the restored Google session reports first, and its `/me` can land
 * after the SightHub one and put the wrong person in the header.
 *
 * Call it before the first render. The hash is read and wiped synchronously, before
 * the router or anything else sees it.
 */
export function completeSightHubSignIn(): Promise<string | null> {
  return (handoff ??= finish())
}

async function finish(): Promise<string | null> {
  const hash = new URLSearchParams(window.location.hash.slice(1))
  const code = hash.get('sso_code')
  const error = hash.get('sso_error')
  if (code === null && error === null) return null

  // Out of the address bar at once: a reload must not replay a spent code, and the
  // history must never hold one.
  const { pathname, search } = window.location
  window.history.replaceState(window.history.state, '', pathname + search)

  if (error !== null || !code) return error || 'no_code'

  try {
    const token = await api.ssoExchange(code, AbortSignal.timeout(20_000))
    // Until the persisted session has been restored, `currentUser` reads null even
    // when someone is signed in.
    await auth.authStateReady()
    // Signed in with Google already: this is a change of account, not a second one.
    if (auth.currentUser) await signOut()
    const { user } = await signInWithCustomToken(auth, token)
    await user.getIdToken(true)
    return null
  } catch (e) {
    // `bad_code` (expired, or used already), a Firebase `auth/…` code, or no answer.
    return e instanceof ApiError || e instanceof FirebaseError ? e.code : 'network'
  }
}
